import { useEffect, useRef, useState, type FormEvent } from 'react';
import type * as Y from 'yjs';
import { useTranslation } from 'react-i18next';
import { reportText, type FigmaImportReport, type SceneStore } from '@os/doc';
import type { Point } from '@os/engine';
import { ApiError, FigmaImportError, type Api } from '../../../lib/api';
import { Button, Dialog, Field, Input } from '../../../ui';
import { DialogClose } from '../../../ui/DialogClose';
import { importBoard, type ImportSession } from '../../export/board-import';
import { importIntoBoard } from '../../export/import-here';
import { archiveOf, figmaFetch, readFigma, type FigmaRead } from './figma-import';

type Here = {
  mode: 'here';
  api: Pick<Api, 'figmaFile' | 'figmaImage'>;
  boardId: string;
  store: SceneStore;
  at: Point;
  ingest(blob: Blob, name: string, sha256: string | null): Promise<string | null>;
  onInserted?: (ids: string[]) => void;
};
type NewBoard = {
  mode: 'new';
  api: Pick<Api, 'figmaFile' | 'figmaImage' | 'createBoard' | 'checkAsset' | 'seedBoard' | 'lifecycle' | 'updateBoard'>;
  orgId: string;
  projectId: string;
  folderId?: string | null;
  session(boardId: string, store: SceneStore): ImportSession;
  onCreated: (boardId: string) => void;
};
type Props = (Here | NewBoard) & { onClose: () => void };

type Step =
  | { kind: 'link'; error: string | null }
  | { kind: 'preview'; read: FigmaRead }
  | { kind: 'running'; done: number; total: number }
  | { kind: 'done'; read: FigmaRead; missing: number; boardId: string | null }
  | { kind: 'cancelled' };

const download = (text: string, name: string) => {
  const url = URL.createObjectURL(new Blob([text], { type: 'text/plain;charset=utf-8' }));
  Object.assign(document.createElement('a'), { href: url, download: name }).click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

/**
 * File › Import › From Figma, and New board › Import from Figma (backlog §12): 1) the link and a read-only personal
 * token, 2) what the file holds, 3) the import with its progress and a way to stop it; then the report. The token
 * lives in this form's state only and is cleared as soon as it was sent.
 */
export function FigmaImportDialog(props: Props) {
  const { t } = useTranslation();
  const [url, setUrl] = useState('');
  const [token, setToken] = useState('');
  const [step, setStep] = useState<Step>({ kind: 'link', error: null });
  const [busy, setBusy] = useState(false);
  const abort = useRef<AbortController | null>(null);
  // The new board's id (P11b Ruling 6): created when the link is first read, trashed unless the import ends.
  const created = useRef<string | null>(null);
  const finished = useRef(false);

  const trash = () => {
    if (props.mode === 'new' && created.current && !finished.current) void props.api.lifecycle('board', 'trash', created.current).catch(() => undefined);
  };
  // Leaving without closing (a route change) stops the import and trashes a board that was never filled.
  useEffect(
    () => () => {
      abort.current?.abort();
      trash();
    },
    [], // `trash` reads refs and the first props only
  );
  const close = () => {
    abort.current?.abort();
    trash();
    props.onClose();
  };

  const errorText = (err: unknown) => {
    if (err instanceof FigmaImportError) return t(`figmaImport.errors.${err.code}`, { seconds: err.retryAfter ?? 60 });
    const code = (err as { code?: string } | null)?.code;
    if (code === 'too_many_pages') return t('figmaImport.errors.too_many_pages');
    if (err instanceof ApiError) return t(err.code === 'forbidden' || err.code === 'not_found' ? 'figmaImport.errors.forbidden' : err.code === 'network' ? 'figmaImport.errors.network' : 'figmaImport.errors.failed');
    return t('figmaImport.errors.failed');
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    const sent = token;
    setToken(''); // never kept once sent
    try {
      let boardId = props.mode === 'here' ? props.boardId : created.current;
      if (props.mode === 'new' && !boardId) {
        boardId = (await props.api.createBoard({ orgId: props.orgId, projectId: props.projectId, ...(props.folderId ? { folderId: props.folderId } : {}), title: t('figmaImport.placeholderTitle') })).id;
        created.current = boardId;
      }
      abort.current = new AbortController();
      setStep({ kind: 'preview', read: await readFigma(props.api, { boardId: boardId!, url, token: sent }, abort.current.signal) });
    } catch (err) {
      if (!abort.current?.signal.aborted) setStep({ kind: 'link', error: errorText(err) });
    } finally {
      setBusy(false);
    }
  };

  const run = async (read: FigmaRead) => {
    const ctrl = new AbortController();
    abort.current = ctrl;
    const total = read.file.assets.length;
    setStep({ kind: 'running', done: 0, total });
    try {
      if (props.mode === 'here') {
        let done = 0;
        const fetchBlob = figmaFetch(props.api, props.boardId, ctrl.signal);
        const out = await importIntoBoard({
          store: props.store, input: archiveOf(read.file), at: props.at, ingest: props.ingest, signal: ctrl.signal,
          fetchBlob: async (u) => {
            const blob = await fetchBlob(u);
            setStep({ kind: 'running', done: ++done, total });
            return blob;
          },
        });
        finished.current = true;
        setStep({ kind: 'done', read, missing: out.missing, boardId: null });
        props.onInserted?.(out.ids.filter((id) => props.store.getObject(id)?.parentId === null));
      } else {
        const boardId = created.current!;
        const out = await importBoard({
          api: props.api, orgId: props.orgId, projectId: props.projectId, input: archiveOf(read.file), session: props.session, signal: ctrl.signal,
          board: { id: boardId, title: read.name || t('figmaImport.placeholderTitle') },
          fetchBlob: figmaFetch(props.api, boardId, ctrl.signal),
          onProgress: (s, done, n) => s === 'assets' && setStep({ kind: 'running', done, total: n }),
        });
        finished.current = true;
        setStep({ kind: 'done', read, missing: out.missing + out.failed, boardId });
      }
    } catch (err) {
      if (ctrl.signal.aborted) {
        // importBoard has already trashed a new board.
        created.current = null;
        setStep({ kind: 'cancelled' });
      } else setStep({ kind: 'link', error: (err as Error).message === 'read_only' ? t('figmaImport.errors.read_only') : errorText(err) });
    }
  };

  // Images already on the board (every page) plus the file's: a board holds about 300 (spec §0).
  const boardImages = props.mode === 'here' ? [...props.store.doc.getMap<Y.Map<unknown>>('objects').values()].filter((m) => m.get('type') === 'image').length : 0;
  const report = (r: FigmaImportReport, incoming = 0) => {
    const left = r.ignored.filter((i) => i.reason !== 'approximate').length;
    const counts = Object.entries(r.imported).filter(([, n]) => n);
    return (
      <div className="flex flex-col gap-1" role="status">
        <p>{t('figmaImport.pages', { count: r.pages })}</p>
        <ul className="list-disc pl-5">
          {counts.map(([type, n]) => (
            <li key={type}>{t('figmaImport.count', { type: t(`a11y.types.${type}`), count: n })}</li>
          ))}
        </ul>
        {r.images.total > 0 && <p>{t('figmaImport.images', { count: r.images.total })}</p>}
        {r.ignored.some((i) => i.reason === 'limit') && <p className="text-danger">{t('figmaImport.imageLimit', { max: 300 })}</p>}
        {incoming > 0 && boardImages + incoming > 300 && <p className="text-danger">{t('figmaImport.boardImageLimit', { max: 300 })}</p>}
        {left > 0 && <p className="text-fg-muted">{t('figmaImport.ignored', { count: left })}</p>}
      </div>
    );
  };

  let body;
  let footer;
  if (step.kind === 'link') {
    body = (
      <form id="figma-link" onSubmit={(e) => void submit(e)} className="flex flex-col gap-4">
        <Field label={t('figmaImport.url')} hint={t('figmaImport.urlHint')}>
          <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://www.figma.com/board/…" inputMode="url" autoComplete="off" required />
        </Field>
        <Field label={t('figmaImport.token')} hint={t('figmaImport.tokenHint')} error={step.error}>
          <Input type="password" value={token} onChange={(e) => setToken(e.target.value)} autoComplete="off" spellCheck={false} required />
        </Field>
        <p className="text-fg-muted">{t('figmaImport.seatWarning')}</p>
      </form>
    );
    footer = (
      <>
        <DialogClose>{t('figmaImport.close')}</DialogClose>
        <Button variant="primary" type="submit" form="figma-link" disabled={busy || !url.trim() || !token.trim()}>
          {busy ? t('figmaImport.reading') : t('figmaImport.continue')}
        </Button>
      </>
    );
  } else if (step.kind === 'preview') {
    body = (
      <div className="flex flex-col gap-3">
        <p className="font-medium">{step.read.name}</p>
        {report(step.read.report, step.read.file.objects.filter((o) => o.type === 'image').length)}
      </div>
    );
    footer = (
      <>
        <DialogClose>{t('figmaImport.close')}</DialogClose>
        <Button variant="primary" onClick={() => void run(step.read)}>
          {t('figmaImport.import')}
        </Button>
      </>
    );
  } else if (step.kind === 'running') {
    body = (
      <div role="status" className="flex flex-col gap-2">
        <p>{step.total ? t('figmaImport.progress', { done: step.done, total: step.total }) : t('figmaImport.progressNoImages')}</p>
        <progress className="w-full accent-accent" max={Math.max(1, step.total)} value={step.done} aria-label={t('figmaImport.progressLabel')} />
      </div>
    );
    footer = <Button onClick={() => abort.current?.abort()}>{t('figmaImport.cancel')}</Button>;
  } else if (step.kind === 'cancelled') {
    body = <p role="status">{t('figmaImport.cancelled')}</p>;
    footer = <DialogClose>{t('figmaImport.close')}</DialogClose>;
  } else {
    const done = step;
    body = (
      <div className="flex flex-col gap-3">
        <p className="font-medium">{t('figmaImport.done')}</p>
        {report(done.read.report)}
        {done.missing > 0 && <p className="text-fg-muted">{t('figmaImport.missing', { count: done.missing })}</p>}
      </div>
    );
    footer = (
      <>
        <Button onClick={() => download(reportText(done.read.report, (k, v) => t(k, v as Record<string, unknown>), done.read.name), `${t('figmaImport.reportFile')}.txt`)}>{t('figmaImport.downloadReport')}</Button>
        {done.boardId && props.mode === 'new' ? (
          <Button variant="primary" onClick={() => props.onCreated(done.boardId!)}>
            {t('figmaImport.openBoard')}
          </Button>
        ) : (
          <DialogClose variant="primary">{t('figmaImport.close')}</DialogClose>
        )}
      </>
    );
  }

  return (
    <Dialog
      title={t('figmaImport.title')}
      description={step.kind === 'link' ? t('figmaImport.description') : undefined}
      onClose={close}
      // While the import runs, closing stops it and the dialog stays to show the outcome (cancelled, or done up to there).
      onCloseRequest={() => {
        if (step.kind !== 'running') return;
        abort.current?.abort();
        return false;
      }}
      footer={footer}
      size="md"
    >
      {body}
    </Dialog>
  );
}
